import { ethers } from 'ethers';
import { EXPLORER_TX } from '../constants/constants';
import {
  apiErrorMessage,
  createEmployee,
  deleteEmployee,
  EditableField,
  recordPayout,
  updateEmployeeField,
} from '../utils/api';
import { contractErrorMessage } from '../utils/contractErrors';
import { getContract } from '../utils/getContract';
import { actions, SignedAction, signAction } from '../utils/signRequest';

/**
 * Действия оператора. Каждое — три шага в одном порядке:
 *
 * 1. Подпись для бэка. До транзакции: откажись оператор от неё после —
 *    контракт уже изменён, а база нет.
 * 2. Транзакция в контракт. Газ оценивает ethers: фиксированный лимит
 *    (был 250 000) перестаёт хватать на выплату с ростом числа сотрудников.
 * 3. Запись в базу с подписью из шага 1.
 *
 * Ошибка любого шага — `Error` с текстом для оператора. Сбой третьего шага
 * сообщает, что контракт уже изменён: таблица в панели в этот момент врёт.
 */

interface Receipt {
  status?: number | null;
  hash: string;
}

async function sendTx(
  send: () => Promise<{ wait: () => Promise<Receipt | null> }>,
) {
  let receipt: Receipt | null;

  try {
    const tx = await send();
    receipt = await tx.wait();
  } catch (error) {
    console.error('Contract call failed:', error);
    throw new Error(contractErrorMessage(error));
  }

  if (receipt?.status !== 1) {
    throw new Error('Транзакция в контракте не прошла');
  }

  return receipt;
}

async function saveToBackend(request: () => Promise<unknown>) {
  try {
    await request();
  } catch (error) {
    console.error('Backend request failed:', error);
    throw new Error(
      `В контракте изменено, но база не обновилась (${apiErrorMessage(error)}). ` +
        'Таблица в панели устарела — сообщите разработчику.',
    );
  }
}

export async function addEmployee(
  fullName: string,
  wallet: string,
  salary: string,
) {
  const headers = await signAction(
    actions.addEmployee(fullName, wallet, salary),
  );
  const contract = await getContract();

  await sendTx(() => contract.addEmployee(wallet, ethers.parseEther(salary)));
  await saveToBackend(() =>
    createEmployee(
      { full_name: fullName, wallet_address: wallet, salary },
      headers,
    ),
  );
}

const FIELD_ACTIONS: Record<
  EditableField,
  { sign: (wallet: string, value: string) => SignedAction; method: string }
> = {
  salary: { sign: actions.updateSalary, method: 'setEmployeeSalary' },
  bonus: { sign: actions.updateBonus, method: 'setEmployeeBonus' },
  penalty: { sign: actions.updatePenalty, method: 'setEmployeePenalty' },
};

export async function updateField(
  wallet: string,
  field: EditableField,
  value: string,
) {
  const { sign, method } = FIELD_ACTIONS[field];
  const headers = await signAction(sign(wallet, value));
  const contract = await getContract();

  await sendTx(() => contract[method](wallet, ethers.parseEther(value)));
  await saveToBackend(() => updateEmployeeField(wallet, field, value, headers));
}

export async function removeEmployee(wallet: string) {
  const headers = await signAction(actions.deleteEmployee(wallet));
  const contract = await getContract();

  // В контракте функция называется `removeEmployee`. Раньше здесь стоял
  // несуществующий `deleteEmployee`, и удаление не работало вообще.
  await sendTx(() => contract.removeEmployee(wallet));
  await saveToBackend(() => deleteEmployee(wallet, headers));
}

/**
 * Выплата всем. `amount` — сумма, которую оператор видел в окне
 * подтверждения; в базу пишется она же. Бэк при записи обнуляет всем премии и
 * штрафы — как контракт. Возвращает ссылку на транзакцию.
 */
export async function payAllSalaries(amount: string): Promise<string> {
  const date = new Date().toISOString().slice(0, 10);
  const headers = await signAction(actions.recordPayout(amount, date));
  const contract = await getContract();

  const receipt = await sendTx(() => contract.payAllSalaries());
  const link = `${EXPLORER_TX}${receipt.hash}`;

  await saveToBackend(() =>
    recordPayout({ date, amount, hash: link }, headers),
  );

  return link;
}
